import React from 'react';
import { AppMark, ELEMENTS, appMark, hashHue } from '@ofself/shell';
import { AppGlyph, PersonFace, GroupFaceIcon } from '../../components/nucleus/faces';
import { Bot, Cog, Globe, KeyRound, Puzzle, SquareUser } from 'lucide-react';
import Inspector, { Band, Facts, Note, Tags } from '../../components/nucleus/Inspector';
import { Row } from '../../components/nucleus/Collection';
import { elementOf } from '../../components/nucleus/cells';
import { usePlace } from '../../nucleus/place';
import {
    kindOf, actorName, actorType, viaApp, appOf, severityOf, detailOf, changesOf, valueText, hm, longWhen,
    resourceDoor, actorDoor, parentDoor, runDoor,
    plainSummary, sentenceOf, whoOf,
} from './activityData';

const NUCLEUS = ELEMENTS.find((e) => e.id === 'nucleus');

// Who acted, as a face: an app's logo, a person's face, the Core for the system — never letters.
export function Face({ entry, size = 30 }) {
    const type = actorType(entry);
    const app = appOf(entry);
    if (app) {
        const el = elementOf({ name: app });
        const hue = el?.hue || hashHue(app);
        const mark = el ? appMark(el, null) : null;
        if (!mark) return <AppGlyph size={size} hue={hue} />;
        return (
            <span className="activity-face activity-face--app" style={{ width: size, height: size, '--face-hue': hue, '--element-hue': hue }}>
                {mark}
            </span>
        );
    }
    if (type === 'system') return <span className="activity-face" style={{ width: size, height: size }}><AppMark app={NUCLEUS} /></span>;
    if (type === 'user') return <PersonFace size={size} />;
    if (type === 'group') return <GroupFaceIcon size={size} />;
    const hue = hashHue(actorName(entry));
    const Icon = type === 'automation' ? Cog : type === 'plugin' ? Puzzle : type === 'token' ? KeyRound
        : type === 'cosm_agent' ? Bot : type === 'public' ? Globe : SquareUser;
    return (
        <span className="activity-face activity-face--delegate" style={{ width: size, height: size, '--face-hue': hue }}>
            <Icon size={size * 0.46} strokeWidth={1.6} />
        </span>
    );
}

// ONE ENTRY OF THE LEDGER, read-only: what it was, who did it and through
// what, what it touched — each of those a door to its own page — what changed,
// and the raw details. A record has nothing to act on, so it has no bar.
export default function EntryInspector({ entry, run, label, meId, realmId, groupName = null, onClose }) {
    const { groupId, navigate, to } = usePlace();
    const kind = kindOf(entry.category);
    const sev = severityOf(entry);
    const refused = entry.success === false;
    const a = entry.actor || {};
    const who = actorDoor(entry, groupId, meId);
    const parent = parentDoor(entry, groupId);
    const res = resourceDoor(entry, groupId);
    const ran = runDoor(entry, groupId);
    const changes = changesOf(entry.details?.changed_fields);
    const details = Object.entries(entry.details || {})
        .filter(([k, v]) => k !== 'changed_fields' && k !== 'actor' && v != null && v !== '')
        .sort(([x], [y]) => x.localeCompare(y))
        .map(([k, v]) => { const [l, val] = detailOf(k, v); return { label: l, value: val }; });
    const realmHref = a.privacy_realm ? to('realms', null, realmId ? `open=${encodeURIComponent(realmId)}` : '') : null;

    return (
        <Inspector
            face={<Face entry={entry} size={48} />}
            tag={`${kind.word} · ${refused ? 'Refused' : 'Allowed'}`}
            title={(groupName && sentenceOf(entry, meId)) || plainSummary(entry, meId, groupName) || 'An entry'}
            sub={`${groupName ? whoOf(entry, meId) : actorName(entry)} · ${longWhen(entry.timestamp)}`}
            onClose={onClose}
        >
            <Band title="This entry">
                <Facts items={[
                    { label: 'Kind', value: kind.word },
                    { label: 'Result', value: refused ? 'Refused' : 'Allowed', tone: refused ? 'live' : undefined },
                    sev && { label: 'Severity', value: sev, tone: sev === 'Warning' ? 'warn' : 'live' },
                    run && run.members.length > 1 && { label: 'Run', value: `One of ${run.members.length}, ${hm(run.members[run.members.length - 1].timestamp)}–${hm(run.lead.timestamp)}` },
                ]} />
                {entry.error_message && <Note tone="live">{entry.error_message}</Note>}
            </Band>

            <Band title="Who acted">
                <Row
                    name={groupName ? whoOf(entry, meId) : actorName(entry)}
                    sub={[label, a.acting_username && `for ${a.acting_username}`].filter(Boolean).join(' · ')}
                    onPress={who ? () => navigate(who) : undefined}
                />
                {parent && viaApp(entry) && (
                    <Row name={viaApp(entry)} sub="The app it acted under" onPress={() => navigate(parent)} />
                )}
                <Tags items={[
                    a.privacy_realm && { key: 'realm', label: `Through ${a.privacy_realm}`, onPress: () => navigate(realmHref) },
                    a.exposure_profile && { key: 'grant', label: `Grant: ${a.exposure_profile}`, onPress: () => navigate(to('realms', 'profiles')) },
                ]} />
            </Band>

            {(res || ran || entry.resource?.endpoint || entry.resource?.title || entry.resource?.label) && (
                <Band title="What it touched">
                    {res
                        ? <Row name={res.label} sub={res.word} onPress={() => navigate(res.href)} />
                        : (entry.resource?.title || entry.resource?.label) && <Row name={entry.resource.title || entry.resource.label} sub={entry.resource.type || undefined} />}
                    {ran && <Row name={ran.label} sub={ran.word} onPress={() => navigate(ran.href)} />}
                    {entry.resource?.endpoint && <Note><code className="activity-code">{entry.resource.endpoint}</code></Note>}
                </Band>
            )}

            {changes.length > 0 && (
                <Band title="What changed" aside={changes.length}>
                    {changes.map((c) => (
                        <div key={c.field} className="activity-change">
                            <span className="activity-change__field">{c.field}</span>
                            {c.diff ? (
                                <span className="activity-change__diff">
                                    <s>{valueText(c.old)}</s>
                                    <span aria-hidden="true">→</span>
                                    <b>{valueText(c.new)}</b>
                                </span>
                            ) : 'value' in c ? <span className="activity-change__diff"><b>{valueText(c.value)}</b></span> : null}
                        </div>
                    ))}
                </Band>
            )}

            <Band title="Details">
                <Facts items={[
                    ...details,
                    entry.source && { label: 'Recorded by', value: entry.source.replace(/_/g, ' ') },
                    { label: 'Action', value: entry.action },
                ]} />
            </Band>
        </Inspector>
    );
}
